import React, { useState, useEffect, useRef } from 'react';
import { useAuthRedirect } from '../utils/authRedirect';
import { useUser } from '../context/UserContext';
import { triggerMonetagVignette } from '../utils/monetagVignette';
import RecipeAddWizard from './RecipeAddWizard';

const WIZARD_STORAGE_KEY = 'workspace_recipe_wizard_v1';

const normalizeStoredRecipe = (candidate) => {
    if (!candidate || typeof candidate !== 'object') return null;
    return {
        ingredients: Array.isArray(candidate.ingredients) ? candidate.ingredients : [],
        instructions: Array.isArray(candidate.instructions) ? candidate.instructions : [],
        notes: Array.isArray(candidate.notes) ? candidate.notes : [],
        title: typeof candidate.title === 'string' ? candidate.title : '',
        suggestedTags: Array.isArray(candidate.suggestedTags) ? candidate.suggestedTags : [],
        extractionMeta: candidate.extractionMeta || null,
    };
};

function RecipeExtractor() {
    const [step, setStep] = useState(0);
    const [input, setInput] = useState('');
    const [recipe, setRecipe] = useState(null);
    const [title, setTitle] = useState('');
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const titleInputRef = useRef(null);
    const hasTriggeredWorkspaceAdRef = useRef(false);

    const handleAuthError = useAuthRedirect();
    const { user, loading: userLoading } = useUser();

    useEffect(() => {
        if (step === 1 && titleInputRef.current) {
            titleInputRef.current.focus();
        }
    }, [step]);

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(WIZARD_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            const restoredRecipe = normalizeStoredRecipe(parsed.recipe);
            const restoredStep = Number.isInteger(parsed.step) ? Math.min(Math.max(parsed.step, 0), 2) : 0;
            setStep(restoredStep);
            setInput(typeof parsed.input === 'string' ? parsed.input : '');
            setRecipe(restoredRecipe);
            setTitle(typeof parsed.title === 'string' ? parsed.title : '');
            setTags(Array.isArray(parsed.tags) ? parsed.tags : []);
            setSaveMessage(typeof parsed.saveMessage === 'string' ? parsed.saveMessage : '');
        } catch (err) {
            sessionStorage.removeItem(WIZARD_STORAGE_KEY);
        }
    }, []);

    useEffect(() => {
        try {
            const payload = {
                step,
                input,
                recipe,
                title,
                tags,
                saveMessage,
            };
            sessionStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(payload));
        } catch (err) {}
    }, [step, input, recipe, title, tags, saveMessage]);

    useEffect(() => {
        if (userLoading) return;
        if (user?.hasNoAds) return;
        if (hasTriggeredWorkspaceAdRef.current) return;
        hasTriggeredWorkspaceAdRef.current = true;
        triggerMonetagVignette('workspace-enter');
    }, [userLoading, user]);

    const handleExtract = async (e) => {
        if (e?.preventDefault) {
            e.preventDefault();
        }

        if (!input.trim()) {
            setError('Please add a recipe link or text before continuing.');
            return;
        }

        setLoading(true);
        setError('');
        setSaveMessage('');
        setRecipe(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/extract-recipe', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ input }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Something went wrong during extraction.');
            }

            const data = await response.json();
            const normalized = {
                ingredients: data.ingredients || [],
                instructions: data.instructions || [],
                notes: data.notes || [],
                title: data.title || '',
                suggestedTags: data.suggestedTags || [],
                extractionMeta: data.extractionMeta || null,
            };

            setRecipe(normalized);
            setTitle(normalized.title);
            setTags(Array.isArray(normalized.suggestedTags) ? normalized.suggestedTags : []);
            setStep(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        const trimmedTitle = title.trim();

        if (!trimmedTitle) {
            setSaveMessage('Please add a title before saving.');
            return;
        }

        setSaving(true);
        setError('');
        setSaveMessage('');

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/recipes', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'x-auth-token': token,
                },
                body: JSON.stringify({ ...recipe, title: trimmedTitle, url: input, tags }),
            });

            if (handleAuthError(response.status)) {
                return;
            }

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Something went wrong while saving.');
            }

            setSaveMessage('Recipe saved successfully!');
            setStep(2);
        } catch (err) {
            setSaveMessage(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleStartOver = (targetStep = 0) => {
        setStep(targetStep);

        if (targetStep === 0) {
            setRecipe(null);
            setTitle('');
            setTags([]);
            setSaveMessage('');
            setError('');
            sessionStorage.removeItem(WIZARD_STORAGE_KEY);
        }
    };

    return (
        <div className="page page-extractor">
            <header className="page-header">
                <span className="page-eyebrow">Workspace</span>
                <h1>Add a recipe in 3 simple steps</h1>
                <p className="page-subtitle">Paste, review, and save. Keep only what helps you cook.</p>
            </header>
            <p className="workspace-note">
                Tip: if extraction looks rough, edit the title and tags first, then refine details in your saved recipe.
            </p>

            <div className="extractor-grid">
                <RecipeAddWizard
                    step={step}
                    input={input}
                    onInputChange={(value) => {
                        setInput(value);
                        if (error) {
                            setError('');
                        }
                    }}
                    onExtract={handleExtract}
                    loading={loading}
                    error={error}
                    recipe={recipe}
                    title={title}
                    onTitleChange={(value) => {
                        setTitle(value);
                        if (saveMessage) {
                            setSaveMessage('');
                        }
                    }}
                    onSave={handleSave}
                    saving={saving}
                    saveMessage={saveMessage}
                    tags={tags}
                    onToggleTag={(tag) => {
                        setTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
                    }}
                    onAddTag={(tag) => {
                        const normalized = String(tag || '').trim().toLowerCase();
                        if (!normalized) return;
                        setTags((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
                    }}
                    onStartOver={handleStartOver}
                    titleInputRef={titleInputRef}
                />
            </div>
        </div>
    );
}

export default RecipeExtractor;
